import { Link } from "react-router-dom";
import { CheckCircle, Circle, Lock, ArrowRight, Sparkle } from "@phosphor-icons/react";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";

/**
 * Module-extensible setup checklist. Renders whatever steps the backend returns,
 * so future HRM/CRM modules can append steps without changing this component.
 */
export function OnboardingChecklist({ status, onNavigate }) {
  if (!status) return null;
  const { steps = [], completed_count, total_count, progress, complete } = status;
  const doneKeys = new Set(steps.filter((s) => s.complete).map((s) => s.key));
  const stepLabel = (k) => steps.find((s) => s.key === k)?.label || k;

  return (
    <Card className="surface-card border-[#E2E8F0]" data-testid="onboarding-checklist">
      <CardContent className="p-6">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Sparkle size={20} weight="duotone" color="#0EA5E9" />
            <div className="font-display text-lg font-semibold">Getting Started</div>
          </div>
          {complete ? (
            <Badge className="bg-[#16A34A]/10 text-[#16A34A] border-0" data-testid="setup-complete-badge">
              <CheckCircle size={14} className="mr-1" weight="fill" /> Setup Complete
            </Badge>
          ) : (
            <span className="text-sm text-[#475569]" data-testid="onboarding-progress-count">
              {completed_count} of {total_count} done
            </span>
          )}
        </div>

        <div className="mt-4">
          <div className="h-2 bg-[#F1F5F9] rounded-full overflow-hidden">
            <div
              className="h-full bg-[#0EA5E9] rounded-full transition-all duration-500"
              style={{ width: `${progress}%` }}
              data-testid="onboarding-progress-bar"
            />
          </div>
        </div>

        <div className="mt-5 space-y-2">
          {steps.map((s, i) => {
            const blockedBy = (s.depends_on || []).filter((k) => !doneKeys.has(k));
            const blocked = !s.complete && blockedBy.length > 0;
            return (
              <div
                key={s.key}
                data-testid={`onboarding-step-${s.key}`}
                className={`flex items-start gap-3 p-3 rounded-xl border transition-colors ${
                  s.complete ? "bg-[#16A34A]/5 border-[#16A34A]/20" : "bg-white border-[#E2E8F0]"
                }`}
              >
                <div className="mt-0.5 shrink-0">
                  {s.complete ? (
                    <CheckCircle size={22} weight="fill" color="#16A34A" data-testid={`onboarding-step-${s.key}-complete`} />
                  ) : blocked ? (
                    <Lock size={22} color="#94A3B8" />
                  ) : (
                    <Circle size={22} color="#0EA5E9" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-mono-data text-[#94A3B8]">{i + 1}</span>
                    <span className={`font-medium text-sm ${s.complete ? "text-[#475569]" : "text-[#0F172A]"}`}>
                      {s.label}
                    </span>
                  </div>
                  <p className="text-xs text-[#475569] mt-0.5">{s.description}</p>
                  {blocked && (
                    <p className="text-xs text-[#D97706] mt-1">
                      Complete {blockedBy.map(stepLabel).join(" & ")} first
                    </p>
                  )}
                </div>
                <div className="shrink-0">
                  {s.complete ? (
                    <Badge variant="outline" className="text-[#16A34A] border-[#16A34A]/30">Done</Badge>
                  ) : blocked ? (
                    <Button size="sm" variant="outline" disabled data-testid={`onboarding-cta-${s.key}`}>
                      {s.cta_label}
                    </Button>
                  ) : (
                    <Link to={s.cta_link} onClick={onNavigate}>
                      <Button size="sm" className="bg-[#0EA5E9] hover:bg-[#0284C7]" data-testid={`onboarding-cta-${s.key}`}>
                        {s.cta_label} <ArrowRight size={14} className="ml-1" />
                      </Button>
                    </Link>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

export default OnboardingChecklist;
